import { HealthInsuranceData, PopulationData, PvCostData, PvSizeData, supabaseClient, VMTData } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export class DataService {
  // Population data
  static async getPopulationData(): Promise<PopulationData[]> {
    const { data, error } = await supabaseClient
      .from('population')
      .select('*')
      .order('year', { ascending: true });
    
    if (error) throw error;
    return data || [];
  }

  // Health insurance data
  static async getHealthInsuranceData(location?: string): Promise<HealthInsuranceData[]> {
    let query = supabaseClient
      .from('percent_no_health_insure')
      .select('*')
      .order('year', { ascending: true });
    
    if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // PV Cost data
  static async getPvCostData(location?: string): Promise<PvCostData[]> {
    let query = supabaseClient
      .from('all_pv_cost')
      .select('*')
      .order('year', { ascending: true });
    
    if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // PV Size data
  static async getPvSizeData(location?: string): Promise<PvSizeData[]> {
    let query = supabaseClient
      .from('all_pv_size')
      .select('*')
      .order('year', { ascending: true });
    
    if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // Get all available locations
  static async getAvailableLocations(): Promise<string[]> {
    const { data, error } = await supabaseClient
      .from('population')
      .select('location_name')
      .order('location_name');
    
    if (error) throw error;
    // Fix TypeScript Set iteration issue
    return Array.from(new Set(data?.map(row => row.location_name) || []));
  }

  // Get city-county mappings from Supabase location service
  static async getCityCountyMappings(): Promise<Record<string, string>> {
    try {
      // Ensure the location service is initialized
      await supabaseLocationService.initialize();
      
      // Get all available locations
      const locations = supabaseLocationService.getAvailableLocations();
      
      // Build city-county mappings
      const mappings: Record<string, string> = {};
      
      locations.forEach(location => {
        if (location.type === 'city' && location.county) {
          mappings[location.name.toLowerCase()] = location.county;
        }
      });
      
      return mappings;
      
    } catch (error) {
      console.error('Error loading city-county mappings from Supabase:', error);
      // Return empty mappings as fallback
      return {};
    }
  }

  // VMT data by mode (raw Replica data)
  static async getVMTData(location?: string, modes?: string[], geofips?: number): Promise<VMTData[]> {
    // Build a strictly filtered query (no broad sampling)
    let query = supabaseClient
      .from('replica_vmt_by_mode')
      .select('*')
      .order('year', { ascending: true });
    
    if (geofips) {
      query = query.eq('geofips', geofips);
    } else if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    if (modes && modes.length > 0) {
      query = query.in('mode', modes);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('VMT query error:', error);
      throw error;
    }
    
    return data || [];
  }

  // VMT data by geofips (raw Replica data)
  static async getVMTDataByGeofips(geofips?: number): Promise<VMTData[]> {
    
    if (!geofips) {
      console.error('Geofips is required for VMT data lookup');
      throw new Error('Geofips is required for VMT data lookup');
    }
    
    try {
      const { data, error } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('*')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (error) {
        console.error('Error fetching VMT data by geofips:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getVMTDataByGeofips:', error);
      throw error;
    }
  }

  // VMT data with scaling factors (2015-2022 backcasting)
  static async getVMTDataWithScaling(geofips?: number, backcastingMethod: 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined' = 'AADT', emfacVersion: '2021' | '2025' = '2025', emfacFleetVersion: '2021' | '2025' = '2025'): Promise<VMTData[]> {
    
    if (!geofips) {
      console.error('Geofips is required for VMT scaling calculation');
      throw new Error('Geofips is required for VMT scaling calculation');
    }

    try {
      // Get the 2023 VMT data from Replica (used for all methods, no scaling)
      let { data: vmt2023Data, error: vmtError } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('*')
        .eq('geofips', geofips)
        .eq('year', 2023);

      if (vmtError) {
        console.error('Error fetching 2023 VMT data:', vmtError);
        throw vmtError;
      }

      // If no 2023 data, try to get the latest available year as fallback
      if (!vmt2023Data || vmt2023Data.length === 0) {
        console.warn(`No 2023 VMT data found for geofips ${geofips}, trying to get latest available year...`);
        const { data: latestVmtData, error: latestError } = await supabaseClient
          .from('replica_vmt_by_mode')
          .select('*')
          .eq('geofips', geofips)
          .order('year', { ascending: false })
          .limit(100);
        
        if (!latestError && latestVmtData && latestVmtData.length > 0) {
          const latestYear = Math.max(...latestVmtData.map((d: any) => d.year));
          console.warn(`Using ${latestYear} VMT data as fallback for geofips ${geofips}`);
          vmt2023Data = latestVmtData.filter((d: any) => d.year === latestYear);
        } else {
          console.error(`No VMT data found for geofips ${geofips} in any year`);
          return [];
        }
      }

      // Determine the baseline year (might be 2023 or earlier if 2023 data not available)
      const baselineYear = vmt2023Data.length > 0 ? vmt2023Data[0].year : 2023;
      console.log(`Using baseline year ${baselineYear} for VMT calculations with method: ${backcastingMethod}`);

      // Fetch AADT data if needed (for AADT or Combined method)
      // Calculate AADT ratios: current_year / next_year (same as EMFAC and Fleet)
      let aadtRatios: any[] = [];
      if (backcastingMethod === 'AADT' || backcastingMethod === 'Combined') {
        try {
          const { data: aadtData, error: aadtError } = await supabaseClient
            .from('aadt_filtered')
            .select('year, value')
            .eq('geofips', geofips)
            .gte('year', 2015)
            .lte('year', 2022)
            .order('year', { ascending: true });
          
          if (aadtError) {
            console.warn('AADT table not available:', aadtError);
          } else if (aadtData && aadtData.length > 0) {
            // Remove duplicates by taking the first occurrence of each year
            const uniqueAadt = new Map<number, number>();
            for (const record of aadtData) {
              if (!uniqueAadt.has(record.year)) {
                uniqueAadt.set(record.year, record.value);
              }
            }
            
            // Calculate ratios: current_year / next_year
            const sortedYears = Array.from(uniqueAadt.keys()).sort((a, b) => a - b);
            for (let i = 0; i < sortedYears.length - 1; i++) {
              const currentYear = sortedYears[i];
              const nextYear = sortedYears[i + 1];
              const currentValue = uniqueAadt.get(currentYear);
              const nextValue = uniqueAadt.get(nextYear);
              
              if (currentValue && nextValue && nextValue > 0) {
                const ratio = currentValue / nextValue;
                aadtRatios.push({
                  year: currentYear,
                  ratio: ratio,
                  aadt_current: currentValue,
                  aadt_next: nextValue
                });
              }
            }
            
            // Sort by year descending for backcasting
            aadtRatios.sort((a, b) => b.year - a.year);
            console.log('AADT ratios calculated:', aadtRatios);
          }
        } catch (err) {
          console.warn('AADT table not available:', err);
        }
      }

      // Fetch Population data if needed (for Population or Combined method)
      let populationRatios: any[] = [];
      if (backcastingMethod === 'Population' || backcastingMethod === 'Combined') {
        try {
          const { data: populationData, error: populationError } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', geofips)
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          console.log(`📊 Population data fetch for geofips ${geofips}:`, {
            records: populationData?.length || 0,
            error: populationError,
            sample: populationData?.slice(0, 3)
          });
          
          if (populationError) {
            console.warn('Population table not available:', populationError);
          } else if (populationData && populationData.length > 0) {
            // Remove duplicates by taking the first occurrence of each year
            const uniquePopulation = new Map<number, number>();
            for (const record of populationData) {
              if (!uniquePopulation.has(record.year)) {
                uniquePopulation.set(record.year, record.population || 0);
              }
            }
            
            // Calculate ratios: current_year / next_year
            const sortedYears = Array.from(uniquePopulation.keys()).sort((a, b) => a - b);
            for (let i = 0; i < sortedYears.length - 1; i++) {
              const currentYear = sortedYears[i];
              const nextYear = sortedYears[i + 1];
              const currentPop = uniquePopulation.get(currentYear);
              const nextPop = uniquePopulation.get(nextYear);
              
              if (currentPop && nextPop && nextPop > 0 && currentYear >= 2015 && currentYear <= 2022) {
                const ratio = currentPop / nextPop;
                populationRatios.push({
                  year: currentYear,
                  ratio: ratio,
                  pop_current: currentPop,
                  pop_next: nextPop
                });
              }
            }
            
            // Sort by year descending for backcasting
            populationRatios.sort((a, b) => b.year - a.year);
            console.log(`📊 Population ratios calculated for geofips ${geofips}:`, populationRatios);
            console.log(`📊 Population ratios count: ${populationRatios.length}, years: ${populationRatios.map(r => r.year).join(', ')}`);
          }
        } catch (err) {
          console.warn('Population table not available:', err);
        }
      }

      // Fetch EMFAC County data if needed (for EMFAC2025 or Combined method)
      let emfacCountyData: any[] = [];
      if (backcastingMethod === 'EMFAC2025' || backcastingMethod === 'Combined') {
        try {
          // Get location info to determine county geofips
          const { data: locationData, error: locationError } = await supabaseClient
            .from('population')
            .select('location_name, geography_type, county')
            .eq('geofips', geofips)
            .limit(1)
            .single();

          if (locationError) {
            console.error('Error fetching location data:', locationError);
          } else {
            let countyGeofips = geofips;
            if (locationData && locationData.geography_type === 'City') {
              const countyName = locationData.county || `${locationData.location_name} County`;
              const { data: countyData, error: countyError } = await supabaseClient
                .from('population')
                .select('geofips')
                .eq('location_name', countyName)
                .eq('geography_type', 'County')
                .limit(1)
                .single();

              if (!countyError && countyData) {
                countyGeofips = countyData.geofips;
              }
            }

            // Query county-level EMFAC data using the same version as 2005-2014
            const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac_county_filtered';
            const { data: emfacData, error: emfacError } = await supabaseClient
              .from(tableName)
              .select('year, backcasting_ratio, total_vmt')
              .eq('geofips', countyGeofips)
              .eq('class', 'LDV')
              .gte('year', 2015)
              .lte('year', 2022)
              .order('year', { ascending: false });

            if (!emfacError && emfacData) {
              emfacCountyData = emfacData.map(record => ({
                year: record.year,
                vmt_ratio: record.backcasting_ratio,
                total_vmt: record.total_vmt
              }));
              console.log(`EMFAC${emfacVersion} County data fetched for backcasting:`, emfacCountyData);
            }
          }
        } catch (err) {
          console.warn('Error fetching EMFAC County data:', err);
        }
      }

      // Fetch EMFAC Fleet data for 2015-2022 backcasting
      // Use local (city-level) fleet data for EMFAC Fleet or Combined method
      let emfacFleetData: any[] = [];
      if (backcastingMethod === 'EMFAC Fleet' || backcastingMethod === 'Combined') {
        try {
          // First, get the location info to determine geography type
          const { data: locationData, error: locationError } = await supabaseClient
            .from('population')
            .select('location_name, geography_type, county')
            .eq('geofips', geofips)
            .limit(1)
            .single();

          if (locationError) {
            console.error('Error fetching location data:', locationError);
            throw locationError;
          }

          // Always use EMFAC2025 Fleet for EMFAC Fleet backcasting
          const fleetTableName = 'emfac2025_fleet_mat';
          const geographyType = locationData?.geography_type === 'City' ? 'City' : 
                                locationData?.geography_type === 'County' ? 'County' : 'City';

          // Query local (city/county) EMFAC Fleet materialized view
          // vmt_ratio = current_year_vmt / next_year_vmt (same as backcasting_ratio)
          const { data: emfacFleetDataRaw, error: emfacFleetError } = await supabaseClient
            .from(fleetTableName)
            .select('year, vmt_ratio, total_vmt')
            .eq('geofips', geofips)
            .eq('geography_type', geographyType)
            .eq('class', 'LDV')
            .in('fuel_type', ['Gasoline', 'Diesel']) // Get main fuel types
            .gte('year', 2015)
            .lte('year', 2022)
            .order('year', { ascending: false });

          if (!emfacFleetError && emfacFleetDataRaw && emfacFleetDataRaw.length > 0) {
            // Aggregate by year (sum total_vmt)
            const yearMap = new Map<number, { total_vmt: number }>();
            for (const record of emfacFleetDataRaw) {
              const year = record.year;
              const existing = yearMap.get(year) || { total_vmt: 0 };
              existing.total_vmt += record.total_vmt || 0;
              yearMap.set(year, existing);
            }
            
            // Calculate ratios from aggregated VMT totals (not from individual row ratios)
            // Ratio = current_year_total_vmt / next_year_total_vmt
            const sortedYears = Array.from(yearMap.keys()).sort((a, b) => b - a);
            emfacFleetData = sortedYears.map((year, index) => {
              const currentVMT = yearMap.get(year)?.total_vmt || 0;
              // Find next year's VMT
              const nextYear = year + 1;
              const nextYearVMT = yearMap.get(nextYear)?.total_vmt || null;
              
              // Calculate ratio from aggregated totals
              const vmt_ratio = (nextYearVMT !== null && nextYearVMT > 0 && currentVMT > 0) 
                ? currentVMT / nextYearVMT 
                : null;
              
              return {
                year,
                vmt_ratio,
                total_vmt: currentVMT
              };
            });
            
            console.log(`EMFAC2025 Fleet ${geographyType} data fetched for backcasting:`, emfacFleetData);
          } else {
            console.warn(`${fleetTableName} ${geographyType} query failed or empty:`, emfacFleetError);
          }
        } catch (err) {
          console.warn('Error fetching EMFAC Fleet data for backcasting:', err);
        }
      }

      // Fetch EMFAC2021 Fleet data for 2015-2022 backcasting
      // Use local (city-level) fleet data for EMFAC2021 Fleet or Combined method
      let emfac2021FleetData: any[] = [];
      if (backcastingMethod === 'EMFAC2021 Fleet' || backcastingMethod === 'Combined') {
        try {
          // First, get the location info to determine geography type
          const { data: locationData, error: locationError } = await supabaseClient
            .from('population')
            .select('location_name, geography_type, county')
            .eq('geofips', geofips)
            .limit(1)
            .single();

          if (locationError) {
            console.error('Error fetching location data:', locationError);
            throw locationError;
          }

          // Use EMFAC2021 Fleet for EMFAC2021 Fleet backcasting
          const fleetTableName = 'emfac_fleet_mat';
          const geographyType = locationData?.geography_type === 'City' ? 'City' : 
                                locationData?.geography_type === 'County' ? 'County' : 'City';

          // Query local (city/county) EMFAC2021 Fleet materialized view
          // Try to get vmt_ratio if available, otherwise calculate from total_vmt
          const { data: emfac2021FleetDataRaw, error: emfac2021FleetError } = await supabaseClient
            .from(fleetTableName)
            .select('year, vmt_ratio, total_vmt')
            .eq('geofips', geofips)
            .eq('geography_type', geographyType)
            .eq('class', 'LDV')
            .in('fuel_type', ['Gasoline', 'Diesel']) // Get main fuel types
            .gte('year', 2015)
            .lte('year', 2022)
            .order('year', { ascending: false });

          if (!emfac2021FleetError && emfac2021FleetDataRaw && emfac2021FleetDataRaw.length > 0) {
            // Aggregate by year (sum total_vmt)
            const yearMap = new Map<number, { total_vmt: number }>();
            for (const record of emfac2021FleetDataRaw) {
              const year = record.year;
              const existing = yearMap.get(year) || { total_vmt: 0 };
              existing.total_vmt += record.total_vmt || 0;
              yearMap.set(year, existing);
            }
            
            // Calculate ratios from aggregated VMT totals (same approach as EMFAC2025 Fleet)
            // Ratio = current_year_total_vmt / next_year_total_vmt
            // Note: Even if vmt_ratio column exists, we calculate from aggregated totals for consistency
            const sortedYears = Array.from(yearMap.keys()).sort((a, b) => b - a);
            emfac2021FleetData = sortedYears.map((year, index) => {
              const currentVMT = yearMap.get(year)?.total_vmt || 0;
              // Find next year's VMT
              const nextYear = year + 1;
              const nextYearVMT = yearMap.get(nextYear)?.total_vmt || null;
              
              // Calculate ratio from aggregated totals (backcasting ratio)
              const vmt_ratio = (nextYearVMT !== null && nextYearVMT > 0 && currentVMT > 0) 
                ? currentVMT / nextYearVMT 
                : null;
              
              return {
                year,
                vmt_ratio,
                total_vmt: currentVMT
              };
            });
            
            console.log(`EMFAC2021 Fleet ${geographyType} data fetched for backcasting:`, emfac2021FleetData);
          } else {
            console.warn(`${fleetTableName} ${geographyType} query failed or empty:`, emfac2021FleetError);
          }
        } catch (err) {
          console.warn('Error fetching EMFAC2021 Fleet data for backcasting:', err);
        }
      }

      // Create backcasted VMT data for each mode using selected backcasting method
      const backcastedData: VMTData[] = [];
      
      for (const mode2023 of vmt2023Data) {
        const mode = mode2023.mode;
        const vmt2023 = mode2023.vmt;
        const baseYear = mode2023.year || baselineYear;
        
        // Start with baseline year data
        backcastedData.push({
          ...mode2023,
          year: baseYear
        });
        
        // If baseline is not 2023, add 2023 data using the same VMT
        if (baseYear < 2023) {
          backcastedData.push({
            ...mode2023,
            year: 2023,
            vmt: vmt2023
          });
        }
        
        // Backcast year by year from 2022 down to 2015
        // All methods use the same sequential ratio application:
        // VMT_year = VMT_year+1 × ratio, where ratio = current_year / next_year
        let previousVMT = vmt2023;
        
        for (let year = 2022; year >= 2015; year--) {
          let scalingFactor: number | null = null;
          
          if (backcastingMethod === 'AADT') {
            // Use AADT ratio (calculated as current_year / next_year)
            const aadtRecord = aadtRatios.find(d => d.year === year);
            if (aadtRecord && aadtRecord.ratio) {
              scalingFactor = aadtRecord.ratio;
            }
          } else if (backcastingMethod === 'EMFAC2025') {
            // Use EMFAC County backcasting_ratio
            const emfacRecord = emfacCountyData.find(d => d.year === year);
            if (emfacRecord && emfacRecord.vmt_ratio !== null) {
              scalingFactor = emfacRecord.vmt_ratio; // backcasting_ratio = current_year / next_year
            }
          } else if (backcastingMethod === 'EMFAC Fleet') {
            // Use EMFAC Fleet vmt_ratio
            const emfacRecord = emfacFleetData.find(d => d.year === year);
            if (emfacRecord && emfacRecord.vmt_ratio !== null) {
              scalingFactor = emfacRecord.vmt_ratio; // vmt_ratio = current_year / next_year
            }
          } else if (backcastingMethod === 'EMFAC2021 Fleet') {
            // Use EMFAC2021 Fleet vmt_ratio
            const emfac2021Record = emfac2021FleetData.find(d => d.year === year);
            if (emfac2021Record && emfac2021Record.vmt_ratio !== null) {
              scalingFactor = emfac2021Record.vmt_ratio; // vmt_ratio = current_year / next_year
            }
          } else if (backcastingMethod === 'Population') {
            // Use Population ratio
            const popRecord = populationRatios.find(d => d.year === year);
            if (popRecord && popRecord.ratio) {
              const popRatio = popRecord.ratio; // ratio = current_year_pop / next_year_pop
              scalingFactor = popRatio;
              console.log(`📊 Population backcasting ${year}: ratio=${popRatio.toFixed(6)}, VMT=${previousVMT.toFixed(0)} → ${(previousVMT * popRatio).toFixed(0)}`);
            } else {
              console.warn(`⚠️ No population ratio found for year ${year}, available years: ${populationRatios.map(r => r.year).join(', ')}`);
            }
          } else if (backcastingMethod === 'Combined') {
            // Average the five ratios (AADT, EMFAC2025, EMFAC Fleet, EMFAC2021 Fleet, Population)
            const ratios: number[] = [];
            
            // Get AADT ratio
            const aadtRecord = aadtRatios.find(d => d.year === year);
            if (aadtRecord && aadtRecord.ratio) {
              ratios.push(aadtRecord.ratio);
            }
            
            // Get EMFAC County ratio
            const emfacRecord = emfacCountyData.find(d => d.year === year);
            if (emfacRecord && emfacRecord.vmt_ratio !== null) {
              ratios.push(emfacRecord.vmt_ratio);
            }
            
            // Get EMFAC Fleet ratio
            const emfacFleetRecord = emfacFleetData.find(d => d.year === year);
            if (emfacFleetRecord && emfacFleetRecord.vmt_ratio !== null) {
              ratios.push(emfacFleetRecord.vmt_ratio);
            }
            
            // Get EMFAC2021 Fleet ratio
            const emfac2021FleetRecord = emfac2021FleetData.find(d => d.year === year);
            if (emfac2021FleetRecord && emfac2021FleetRecord.vmt_ratio !== null) {
              ratios.push(emfac2021FleetRecord.vmt_ratio);
            }
            
            // Get Population ratio
            const popRecord = populationRatios.find(d => d.year === year);
            if (popRecord && popRecord.ratio) {
              ratios.push(popRecord.ratio);
            }
            
            // Calculate average if we have at least one ratio
            if (ratios.length > 0) {
              scalingFactor = ratios.reduce((sum, r) => sum + r, 0) / ratios.length;
              // Removed excessive logging - ratios are calculated correctly
            }
          }
          
          if (scalingFactor !== null && scalingFactor > 0) {
            // Apply ratio sequentially: VMT_year = VMT_year+1 × scaling_factor
            const backcastedVMT = Math.round(previousVMT * scalingFactor);
            
            backcastedData.push({
              ...mode2023,
              year: year,
              vmt: backcastedVMT
            });
            
            // Update previous VMT for next iteration
            previousVMT = backcastedVMT;
          } else {
            console.warn(`⚠️ No scaling factor available for year ${year} with method ${backcastingMethod}, using previous year's VMT`);
            // Use previous year's VMT if ratio not available
            backcastedData.push({
              ...mode2023,
              year: year,
              vmt: previousVMT
            });
          }
        }
      }

      console.log(`✅ getVMTDataWithScaling: Created ${backcastedData.length} records for years:`, Array.from(new Set(backcastedData.map(d => d.year))).sort());
      console.log(`   Method: ${backcastingMethod}, Years range: ${Math.min(...backcastedData.map(d => d.year))} - ${Math.max(...backcastedData.map(d => d.year))}`);

      return backcastedData;
      
    } catch (error) {
      console.error('Error in getVMTDataWithScaling:', error);
      throw error;
    }
  }

  // EMFAC backcasting data for VMT historical reconstruction
  static async getEMFACBackcastingData(location: string, years: number[] = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], emfacVersion: '2021' | '2025' = '2025'): Promise<any[]> {
    
    try {
      // First, determine the county for the given location
      let countyName = location;
      
      // If it's a city, we need to map it to its county
      if (!location.toLowerCase().includes('county')) {
        
        // Use the existing city-county mapping logic from getCityCountyMappings
        const mappings = await this.getCityCountyMappings();
        const locationKey = location.toLowerCase();
        
        // Try to find a mapping
        const mapping = Object.entries(mappings).find(([city, county]) => 
          city.toLowerCase().includes(locationKey) || 
          locationKey.includes(city.toLowerCase())
        );
        
        if (mapping) {
          countyName = mapping[1];
        } else {
        }
      }
      
      // Select table based on EMFAC version
      const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac_county_filtered';
      
      // Fetch EMFAC data for the county
      const { data, error } = await supabaseClient
        .from(tableName)
        .select('*')
        .ilike('location_name', `%${countyName}%`)
        .in('year', years)
        .in('class', ['LDV', 'HDV']) // Light Duty Vehicles and Heavy Duty Vehicles
        .order('year', { ascending: true });
      
      if (error) {
        console.error('EMFAC query error:', error);
        // If the table doesn't exist or there's an error, return empty array instead of throwing
        console.warn('EMFAC table may not exist yet, returning empty data');
        return [];
      }
      
      return data || [];
      
    } catch (error) {
      console.error('Error in getEMFACBackcastingData:', error);
      console.warn('Returning empty EMFAC data due to error');
      return [];
    }
  }

  // EMFAC backcasting data by geofips
  static async getEMFACBackcastingDataByGeofips(geofips?: number, years: number[] = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], method: 'EMFAC2025' | 'Population' | 'Combined' = 'EMFAC2025'): Promise<any[]> {
    
    if (!geofips) {
      console.error('Geofips is required for EMFAC data lookup');
      throw new Error('Geofips is required for EMFAC data lookup');
    }
    
    try {
      // First, get the location info to determine if it's a city or county
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('location_name, geography_type, county')
        .eq('geofips', geofips)
        .limit(1);

      if (locationError) {
        console.error('Error fetching location data for geofips:', locationError);
        throw locationError;
      }

      if (!locationData || locationData.length === 0) {
        return [];
      }

      const locationInfo = locationData[0];

      // For EMFAC data, we need to use county-level data
      // If it's a city, we need to find the corresponding county
      let countyGeofips = geofips;
      
      if (locationInfo.geography_type === 'City') {
        // For cities, we need to find the county geofips
        
        // Get the county name from the location info
        const countyName = locationInfo.county || `${locationInfo.location_name} County`;
        
        // Find the county geofips
        const { data: countyData, error: countyError } = await supabaseClient
          .from('population')
          .select('geofips')
          .eq('location_name', countyName)
          .eq('geography_type', 'County')
          .limit(1);

        if (countyError) {
          console.error('Error finding county geofips:', countyError);
          return [];
        }

        if (countyData && countyData.length > 0) {
          countyGeofips = countyData[0].geofips;
        }
      }

      if (method === 'Combined') {
        // Fetch both EMFAC2025 and Population data, then average the ratios
        const allYears = [...years, 2015];
        
        // Fetch EMFAC2025 data
        const tableName = 'emfac2025_county_filtered';
        const { data: emfacData, error: emfacError } = await supabaseClient
          .from(tableName)
          .select('*')
          .eq('geofips', countyGeofips)
          .in('year', allYears)
          .in('class', ['LDV', 'HDV'])
          .order('year', { ascending: true })
          .limit(50000);

        // Fetch Population data
        const { data: popData, error: popError } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', geofips)
          .in('year', allYears)
          .order('year', { ascending: true });

        if (emfacError) {
          console.error('Error fetching EMFAC2025 data for Combined method:', emfacError);
        }
        if (popError) {
          console.error('Error fetching Population data for Combined method:', popError);
        }

        // Process EMFAC2025 ratios
        const emfacRatios = new Map<number, number>();
        if (emfacData && emfacData.length > 0) {
          for (const year of years) {
            const yearLDVData = emfacData.find(record => record.year === year && record.class === 'LDV');
            if (yearLDVData) {
              let ratio: number | null = null;
              if (yearLDVData.backcasting_ratio !== null && yearLDVData.backcasting_ratio !== undefined) {
                ratio = yearLDVData.backcasting_ratio;
              } else {
                const emfac2015LDV = emfacData.find(record => record.year === 2015 && record.class === 'LDV');
                const emfac2015LDVVMT = emfac2015LDV ? emfac2015LDV.total_vmt : 0;
                if (emfac2015LDVVMT > 0 && yearLDVData.total_vmt) {
                  ratio = yearLDVData.total_vmt / emfac2015LDVVMT;
                }
              }
              if (ratio !== null) {
                emfacRatios.set(year, ratio);
              }
            }
          }
        }

        // Process Population ratios
        const popRatios = new Map<number, number>();
        if (popData && popData.length > 0) {
          const popByYear = new Map<number, number>();
          for (const record of popData) {
            popByYear.set(record.year, record.population);
          }
          for (const year of years) {
            const currentPop = popByYear.get(year);
            const nextYear = year + 1;
            const nextPop = popByYear.get(nextYear);
            if (currentPop && nextPop && nextPop > 0) {
              const ratio = currentPop / nextPop;
              popRatios.set(year, ratio);
            }
          }
        }

        // Average the ratios for each year
        const ratioData = [];
        for (const year of years) {
          const ratios: number[] = [];
          
          const emfacRatio = emfacRatios.get(year);
          if (emfacRatio !== undefined) {
            ratios.push(emfacRatio);
          }
          
          const popRatio = popRatios.get(year);
          if (popRatio !== undefined) {
            ratios.push(popRatio);
          }

          if (ratios.length > 0) {
            const avgRatio = ratios.reduce((sum, r) => sum + r, 0) / ratios.length;
            ratioData.push({
              year: year,
              ratio: avgRatio,
              total_vmt: 0
            });
            // Removed excessive logging - ratios are calculated correctly
          }
        }

        return ratioData;
      } else if (method === 'Population') {
        // Fetch population data for the location (use the original geofips, not county)
        const allYears = [...years, 2015]; // Include 2015 for baseline calculation
        console.log(`Fetching Population data for geofips:`, geofips, 'years:', allYears);
        
        const { data: popData, error: popError } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', geofips)
          .in('year', allYears)
          .order('year', { ascending: true });

        if (popError) {
          console.error('Error fetching population data by geofips:', popError);
          throw popError;
        }

        console.log('Raw Population data from database:', popData);

        // Calculate population-based ratios: current_year_pop / next_year_pop
        if (popData && popData.length > 0) {
          const ratioData = [];
          const popByYear = new Map<number, number>();
          for (const record of popData) {
            popByYear.set(record.year, record.population);
          }

          for (const year of years) {
            const currentPop = popByYear.get(year);
            const nextYear = year + 1;
            const nextPop = popByYear.get(nextYear);

            if (currentPop && nextPop && nextPop > 0) {
              const ratio = currentPop / nextPop;
              ratioData.push({
                year: year,
                ratio: ratio,
                total_vmt: 0 // Not applicable for population method
              });
              console.log(`Population ratio for ${year}: ${ratio.toFixed(6)} (${currentPop.toLocaleString()} / ${nextPop.toLocaleString()})`);
            } else {
              console.log(`No population data found for year ${year} or ${nextYear}`);
            }
          }

          return ratioData;
        }

        return [];
      } else {
        // EMFAC2025 method (default)
        // Fetch EMFAC data for the county geofips (include 2015 for baseline)
        const allYears = [...years, 2015]; // Include 2015 for baseline calculation
        const tableName = 'emfac2025_county_filtered';
        console.log(`Fetching EMFAC2025 data for county geofips:`, countyGeofips, 'years:', allYears);
        const { data, error } = await supabaseClient
          .from(tableName)
          .select('*')
          .eq('geofips', countyGeofips)
          .in('year', allYears)
          .in('class', ['LDV', 'HDV']) // Light Duty Vehicles and Heavy Duty Vehicles
          .order('year', { ascending: true })
          .limit(50000); // High limit to ensure we get all data

        if (error) {
          console.error('Error fetching EMFAC data by geofips:', error);
          throw error;
        }

        console.log('Raw EMFAC data from database:', data);

        // Process the data to create ratio fields for backcasting
        if (data && data.length > 0) {
          // Use backcasting_ratio from the filtered view if available (preferred method)
          // Otherwise, calculate manually from total_vmt
          const ratioData = [];
          for (const year of years) {
            const yearLDVData = data.find(record => record.year === year && record.class === 'LDV');
            if (yearLDVData) {
              // Prefer backcasting_ratio from filtered view if available
              let ratio: number | null = null;
              if (yearLDVData.backcasting_ratio !== null && yearLDVData.backcasting_ratio !== undefined) {
                ratio = yearLDVData.backcasting_ratio;
                console.log(`EMFAC2025 backcasting_ratio for ${year}: ${ratio} (from filtered view)`);
              } else {
                // Fallback: calculate ratio manually if backcasting_ratio not available
                const emfac2015LDV = data.find(record => record.year === 2015 && record.class === 'LDV');
                const emfac2015LDVVMT = emfac2015LDV ? emfac2015LDV.total_vmt : 0;
                if (emfac2015LDVVMT > 0 && yearLDVData.total_vmt) {
                  ratio = yearLDVData.total_vmt / emfac2015LDVVMT;
                  console.log(`EMFAC2025 ratio calculation for ${year}: yearLDVVMT=${yearLDVData.total_vmt}, emfac2015LDVVMT=${emfac2015LDVVMT}, ratio=${ratio} (calculated)`);
                }
              }
              
              if (ratio !== null) {
                ratioData.push({
                  year: year,
                  ratio: ratio,
                  total_vmt: yearLDVData.total_vmt || 0
                });
              } else {
                console.log(`No EMFAC2025 LDV data or ratio found for year ${year}`);
              }
            } else {
              console.log(`No EMFAC2025 LDV data found for year ${year}`);
            }
          }

          return ratioData;
        }

        return [];
      }
      
    } catch (error) {
      console.error('Error in getEMFACBackcastingDataByGeofips:', error);
      console.warn('Returning empty EMFAC data due to error');
      return [];
    }
  }

  // VMT per capita data using the exact same VMT values as the main VMT chart
  static async getVMTPerCapitaData(geofips?: number): Promise<any[]> {
    if (!geofips) {
      console.error('Geofips is required for VMT per capita calculation');
      throw new Error('Geofips is required for VMT per capita calculation');
    }

    try {
      console.log('Calculating VMT per capita using exact same VMT as main chart for geofips:', geofips);

      // Get VMT data using the EXACT same method as the main VMT chart
      const locationVMTData = await this.getVMTDataWithScaling(geofips);
      
      if (!locationVMTData || locationVMTData.length === 0) {
        console.error('No VMT data found for location with geofips:', geofips);
        throw new Error('No VMT data available for this location');
      }

      // Get population data for the selected location
      const locationName = locationVMTData[0]?.location_name;
      console.log('Location name from VMT data:', locationName);

      // Try to get population by geofips first, then fallback to location_name lookup
      let locationPopulationData: any[] = [];
      
      const { data: popByGeofips, error: popGeofipsError } = await supabaseClient
        .from('population')
        .select('year, population, geofips')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (popGeofipsError || !popByGeofips || popByGeofips.length === 0) {
        console.warn('No population data found by geofips, trying location name lookup...');
        
        const normalizedLocationName = locationName.toLowerCase().trim();
        const { data: popByName, error: popNameError } = await supabaseClient
          .from('population')
          .select('year, population, location_name, geofips')
          .or(`location_name.ilike.%${normalizedLocationName}%`)
          .order('year', { ascending: true });

        if (popNameError || !popByName || popByName.length === 0) {
          console.error('No population data found by location name either');
          throw new Error(`No population data found for ${locationName} (geofips: ${geofips})`);
        } else {
          locationPopulationData = popByName;
          console.log('Found population data by location name:', popByName.length, 'records');
        }
      } else {
        locationPopulationData = popByGeofips;
        console.log('Found population data by geofips:', popByGeofips.length, 'records');
      }

      // Use the exact same VMT calculation as the main VMT chart:
      // Sum PRIVATE_AUTO and ON_DEMAND_AUTO modes for each year
      const vmtByYear = locationVMTData
        .filter(d => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_vmt += curr.vmt;
          } else {
            acc.push({
              year: curr.year,
              total_vmt: curr.vmt,
              location_name: curr.location_name
            });
          }
          return acc;
        }, [])
        .sort((a: any, b: any) => a.year - b.year);

      console.log('VMT by year (matching main chart logic):', vmtByYear);

      // Calculate VMT per capita for selected location (VMT from main chart ÷ population)
      const locationVMTPerCapita = vmtByYear.map((vmt: any) => {
        const population = locationPopulationData.find(p => p.year === vmt.year);
        const vmtPerCapita = population && population.population > 0 ? vmt.total_vmt / population.population : 0;
        
        console.log(`${locationName} Year ${vmt.year}: VMT=${vmt.total_vmt.toLocaleString()}, Population=${population?.population?.toLocaleString() || 'NOT FOUND'}, VMT per capita=${vmtPerCapita.toFixed(1)}`);
        
        return {
          year: vmt.year,
          vmt_per_capita: vmtPerCapita,
          location: 'Selected Location'
        };
      });

      // Calculate California average using the same approach
      // Get California state-level VMT using same methodology
      console.log('Calculating California average using same VMT methodology...');
      
      // Use LA County as a large representative sample and scale appropriately
      const californiaProxyVMT = await this.getVMTDataWithScaling(6037); // Los Angeles County
      
      const californiaVMTByYear = californiaProxyVMT
        .filter(d => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_vmt += curr.vmt;
          } else {
            acc.push({
              year: curr.year,
              total_vmt: curr.vmt
            });
          }
          return acc;
        }, [])
        .sort((a: any, b: any) => a.year - b.year);

      // Get California total population
      const { data: calPopData, error: calPopError } = await supabaseClient
        .from('population')
        .select('year, population')
        .order('year', { ascending: true });

      if (calPopError) {
        console.error('Error fetching California population data:', calPopError);
        throw calPopError;
      }

      // Aggregate California population by year
      const californiaPopByYear = calPopData
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_population += curr.population || 0;
          } else {
            acc.push({
              year: curr.year,
              total_population: curr.population || 0
            });
          }
          return acc;
        }, []);

      // Calculate California VMT per capita
      // Scale LA County VMT to represent California (LA County is ~25% of CA population)
      const californiaVMTPerCapita = californiaVMTByYear.map((vmt: any) => {
        const population = californiaPopByYear.find((p: any) => p.year === vmt.year);
        
        // Scale LA County VMT to approximate California state total
        // LA County represents about 25% of California's population and has higher VMT than state average
        const scalingFactor = 4.0; // Rough approximation to scale from LA County to state level
        const stateVMT = vmt.total_vmt * scalingFactor;
        
        const vmtPerCapita = population && population.total_population > 0 ? stateVMT / population.total_population : 0;
        
        console.log(`California Year ${vmt.year}: Scaled VMT=${stateVMT.toLocaleString()}, Population=${population?.total_population?.toLocaleString() || 'NOT FOUND'}, VMT per capita=${vmtPerCapita.toFixed(1)}`);
        
        return {
          year: vmt.year,
          vmt_per_capita: vmtPerCapita,
          location: 'California Average'
        };
      });

      // Combine the data
      const combinedData = [...locationVMTPerCapita, ...californiaVMTPerCapita];

      console.log('Final VMT per capita data - using exact same VMT as main chart:');
      console.log('Location data points:', locationVMTPerCapita.length);
      console.log('California data points:', californiaVMTPerCapita.length);

      return combinedData;

    } catch (error) {
      console.error('Error in getVMTPerCapitaData:', error);
      throw error;
    }
  }
}
